import type { HydraulicSystem } from '@shared/hydraulic'
import { resolveDensityForEdge, WATER_DENSITY_KGM3 } from '@shared/hydraulic'
import { readSI } from '@shared/hydraulic'
import { suggestPipeSize } from './pipe-sizing'

export interface AutoSizeSuggestion {
  edgeId: string
  edgeName: string
  currentSizeId: string
  currentSizeName: string
  suggestedSizeId: string
  suggestedSizeName: string
  currentVelocity: number    // m/s
  suggestedVelocity: number  // m/s
  isCompromise: boolean
  violation: 'too_fast' | 'too_slow'
}

interface CalcResults {
  status: string
  edgeResults: { id: string; flow: number; sourcePressure: number; targetPressure: number }[]
  vertexPressures: Record<string, number>
}

/**
 * Compute auto-size suggestions for all pipes that violate the velocity constraint.
 *
 * This is called after every successful calculation. It reads the system's
 * design_constraints.max_velocity, converts to m/s, and checks each pipe edge.
 */
export function computeAutoSizeSuggestions(
  system: HydraulicSystem,
  calcResults: CalcResults,
): AutoSizeSuggestion[] {
  // Default fallback matches VelocityAnalyzer's VELOCITY_HIGH threshold (3.0 m/s)
  const DEFAULT_MAX_VELOCITY_MS = 3.0

  const constraints = system.design_constraints
  const maxVelocityMs = constraints?.max_velocity
    ? readSI(constraints.max_velocity, 'velocity')
    : DEFAULT_MAX_VELOCITY_MS
  const minVelocityMs = 0 // future: constraints.min_velocity

  const suggestions: AutoSizeSuggestion[] = []

  for (const edge of system.edges) {
    if (edge.type !== 'pipe' && edge.type !== 'compressible_pipe') continue

    // `edge` is now narrowed to PipeEdge | CompressiblePipeEdge — both expose
    // fluid_zone_id and pipe_specification_id directly without a cast.
    const edgeResult = calcResults.edgeResults.find((r): boolean => r.id === edge.id)
    if (!edgeResult) continue

    const density = resolveDensityForEdge(system, edge, WATER_DENSITY_KGM3)

    const spec = system.pipe_specifications.find(
      (ps): boolean => ps.id === edge.pipe_specification_id,
    )
    if (!spec) continue

    const currentSizeId = edge.pipe_size_id
    const currentSize = spec.sizes.find((s): boolean => s.id === currentSizeId)
    if (!currentSize) continue

    // Compute current velocity
    const absFlow = Math.abs(edgeResult.flow)
    const volumetricFlow = absFlow / density

    if (volumetricFlow < 1e-10) continue

    const idMeters = innerDiameterMeters(currentSize)
    if (idMeters <= 0) continue
    const area = Math.PI * (idMeters / 2) ** 2
    const currentVelocity = volumetricFlow / area

    // Check if velocity is outside the range
    const isTooFast = currentVelocity > maxVelocityMs
    const isTooSlow = minVelocityMs > 0 && currentVelocity < minVelocityMs
    if (!isTooFast && !isTooSlow) continue

    // Get suggestion
    const result = suggestPipeSize(
      edgeResult.flow,
      density,
      spec,
      maxVelocityMs,
      minVelocityMs,
    )
    if (!result) continue

    // Skip if suggested is same as current
    if (result.sizeId === currentSizeId) continue

    suggestions.push({
      edgeId: edge.id,
      edgeName: edge.name ?? edge.id,
      currentSizeId,
      currentSizeName: currentSize.name,
      suggestedSizeId: result.sizeId,
      suggestedSizeName: result.sizeName,
      currentVelocity,
      suggestedVelocity: result.velocity,
      isCompromise: result.isCompromise,
      violation: isTooFast ? 'too_fast' : 'too_slow',
    })
  }

  return suggestions
}

/** Convert inner diameter to meters. #850: `readSI` is the one length table —
 *  this used to be a hand copy of pipe-sizing.ts's copy. */
function innerDiameterMeters(size: { inner_diameter?: { value: number; unit: string } }): number {
  return size.inner_diameter ? readSI(size.inner_diameter, 'length') : 0
}
